import React from 'react';
import { motion } from 'framer-motion';
import { FiEye, FiShield, FiUsers, FiAward, FiArrowRight } from 'react-icons/fi';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

function About() {
  const values = [
    { icon: <FiEye className="w-6 h-6" />, title: 'Transparency', desc: 'We believe in clear communication, honest advice and fair dealing with every client.', color: 'from-primary-500 to-primary-600' },
    { icon: <FiAward className="w-6 h-6" />, title: 'Quality', desc: 'Every case is handled with professionalism, legal expertise and attention to detail.', color: 'from-teal-500 to-teal-600' },
    { icon: <FiUsers className="w-6 h-6" />, title: 'Accessibility', desc: 'Legal help should be easily available to everyone across Bihar, whenever needed.', color: 'from-accent-500 to-accent-600' },
    { icon: <FiShield className="w-6 h-6" />, title: 'Confidentiality', desc: 'Client privacy and confidentiality are maintained at every stage of the case.', color: 'from-primary-400 to-primary-500' },
  ];

  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '1000+', label: 'Cases Handled' },
    { value: 'Multiple', label: 'Practice Areas' },
    { value: 'Strong', label: 'Client Satisfaction' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-20 md:py-28 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-bold text-white mb-4">About RKS Legal Office</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg max-w-xl mx-auto">Trusted legal services across Bihar</motion.p>
        </div>
      </div>

      {/* Mission */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-4">Our Mission</span>
          <p className="text-lg md:text-xl text-secondary-600 leading-relaxed">
            At RKS Legal Office, we believe everyone deserves access to quality legal advice. Our mission is to provide reliable, practical and result-oriented legal solutions across Bihar. We aim to bridge the gap between people seeking justice and effective legal representation by offering clear guidance, strong advocacy and timely support.
          </p>
        </motion.div>
      </div>

      {/* Values */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">Our Values</h2>
          <p className="text-secondary-500">The principles that guide everything we do</p>
        </div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 text-center hover:shadow-card-hover transition-shadow">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${v.color} flex items-center justify-center text-white mx-auto mb-4`}>{v.icon}</div>
              <h3 className="font-semibold text-secondary-900 mb-2">{v.title}</h3>
              <p className="text-sm text-secondary-500">{v.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Stats */}
      <div className="relative overflow-hidden py-16" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 100%)' }}>
        <div className="max-w-6xl mx-auto px-4">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center">
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-1">{s.value}</h3>
                <p className="text-white/50 text-sm">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">Join Our Legal Network</h2>
          <p className="text-secondary-500 mb-8 max-w-lg mx-auto">Get trusted legal assistance and professional guidance for your legal matters across Bihar.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+917970809406" className="btn-primary py-3.5 px-8 text-base gap-2">
              Call Now <FiArrowRight className="w-4 h-4" />
            </a>
            <a href="https://wa.me/917970809406" target="_blank" rel="noopener noreferrer" className="py-3.5 px-8 text-base gap-2 rounded-xl font-semibold border-2 border-secondary-200 text-secondary-700 hover:bg-secondary-50 transition-colors inline-flex items-center">
              WhatsApp Consultation
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default About;
